import { SchoolUser, SchoolUserType } from '@core/models/domain/account.domain';
import { Device, DeviceStatus } from '@core/models/domain/device.domain';
import { SchoolGroupStatus } from '@core/models/domain/group.domain';

/**
 * Get the displayed status for a user.
 * For pupils: Check school enrollment status first
 *   - If school status is EXEMPT, display EXEMPT
 *   - If school status is INACTIVE, display INACTIVE
 *   - If school status is ACTIVE, check user account status and devices
 *     (ACTIVE account with no ENROLLED device means INACTIVE: the pupil cannot be restricted)
 * For non-pupils: Display the user account status
 *
 * Pure function shared by the user-list component and the selectActiveUsers selector.
 */
export function getDisplayedUserStatus(user: SchoolUser): string {
  const isPupil = user.schoolUserType === SchoolUserType.PUPIL;
  const schoolUserStatus = user.schoolUserStatus; // School enrollment status
  const userAccountStatus = user.userInfo?.status; // User account status

  if (isPupil) {
    // An exemption is the school's own decision and outranks everything else: the pupil is not
    // expected to enrol and their device is never restricted, so neither the account status nor
    // whether they happen to own a device changes what is shown.
    if (schoolUserStatus === 'EXEMPT') {
      return 'EXEMPT';
    }

    // For pupils with INACTIVE school enrollment status, always show INACTIVE
    if (schoolUserStatus === 'INACTIVE') {
      return 'INACTIVE';
    }

    // For pupils with ACTIVE school enrollment status
    if (schoolUserStatus === 'ACTIVE') {
      // Check the user account status
      if (userAccountStatus === 'ACTIVE') {
        return hasEnrolledDevice(user) ? 'ACTIVE' : 'INACTIVE';
      }
      // If account is not active, show the account status
      return userAccountStatus || 'PENDING';
    }

    // For any other school status (including PENDING), return school status
    return schoolUserStatus || 'PENDING';
  }

  // For non-pupil users, return the user account status
  return userAccountStatus || '';
}

/**
 * True for a pupil still on the school roster whose account is active but who has no ENROLLED
 * device. They display as INACTIVE, yet unlike a removed pupil they stay in the list with their
 * groups, because the fix is on the parent's side (enrol a device), not the school's.
 */
export function isPupilWithoutEnrolledDevice(user: SchoolUser): boolean {
  return user.schoolUserType === SchoolUserType.PUPIL
    && user.schoolUserStatus === 'ACTIVE'
    && user.userInfo?.status === 'ACTIVE'
    && !hasEnrolledDevice(user);
}

/** True when the user is displayed INACTIVE because they were removed, not because of devices. */
export function isRemovedUser(user: SchoolUser): boolean {
  return getDisplayedUserStatus(user) === 'INACTIVE' && !isPupilWithoutEnrolledDevice(user);
}

/**
 * True for a pupil on the roll (school status ACTIVE or PENDING, account not deactivated) who is in no
 * ACTIVE group, typically left behind in a group the school deleted. School tasks target groups, so no
 * restriction reaches them (DIGI-1701). EXEMPT pupils are never restricted anyway and are not counted.
 * Only a group of the pupil's own school counts. Same rule as the backend remove-group guard and
 * StrandedPupilGroupRunner.
 */
export function isPupilWithoutActiveGroup(user: SchoolUser): boolean {
  if (user.schoolUserType !== SchoolUserType.PUPIL) {
    return false;
  }
  if (user.schoolUserStatus !== 'ACTIVE' && user.schoolUserStatus !== 'PENDING') {
    return false;
  }
  const accountStatus = user.userInfo?.status;
  if (accountStatus === 'INACTIVE' || accountStatus === 'INACTIVE_PENDING') {
    return false;
  }
  return !user.schoolGroups?.some(group => group.status === SchoolGroupStatus.ACTIVE
    && group.schoolAccountId === user.schoolAccountId);
}

/**
 * True when the user holds a device the school can restrict, the same rule the backend coverage
 * uses (ChildCoverageQueryHandler.reasonFor): ENROLLED and not disabled for the school.
 * Shared by the user list, the selector and the Home enrolment figures so the pages agree.
 */
export function hasEnrolledDevice(user: SchoolUser): boolean {
  return !!user.devices?.some(device => isEnrolled(device) && device.disabledForSchool !== true);
}

/**
 * When a pupil shown ACTIVE has gone quiet: every phone the school can restrict carries the
 * backend's "not in touch" flag (DIGI-1747). Returns the latest last sign of life among them, or
 * null when it is not known; undefined when the pupil is not quiet at all. The app deleted from a
 * phone looks exactly like this, so it is the one sign a school gets of it before the phone is
 * unpaired.
 */
export function pupilQuietSince(user: SchoolUser): Date | null | undefined {
  const restrictable = (user.devices ?? []).filter(device => isEnrolled(device) && device.disabledForSchool !== true);
  if (restrictable.length === 0 || restrictable.some(device => !device.staleAlertSeverity)) {
    return undefined;
  }
  const times = restrictable.map(device => device.staleSince).filter(isValidDate) as Date[];
  return times.length ? new Date(Math.max(...times.map(time => time.getTime()))) : null;
}

/** Enrolled, counting a legacy row with no status but a valid enrolment date (shared with the device list). */
export function isEnrolled(device: Device): boolean {
  if (device.status) {
    return device.status === DeviceStatus.ENROLLED;
  }
  // Legacy rows with no status: the enrolment stamp is only written when enrolment completed.
  return isValidDate(device.enrolmentDatetime);
}

function isValidDate(value: Date | undefined): boolean {
  return value instanceof Date && !isNaN(value.getTime());
}
